/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { ActionButton, Tooltip, Callout } from "@octopusdeploy/design-system-components";
import type { AccountResource, CertificateResource, EnvironmentResource, LibraryVariableSetResource, ProjectResource, TenantLibraryVariable, TenantMissingVariableResource, TenantProjectVariable, TenantResource, TenantVariableTemplateResource, WorkerPoolResource, } from "@octopusdeploy/octopus-server-client";
import { AccountType, Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import cn from "classnames";
import { cloneDeep, memoize, sortBy, take, uniq } from "lodash";
import * as React from "react";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import type { AnalyticActionDispatcher } from "~/analytics/Analytics";
import { Action, useAnalyticActionDispatch } from "~/analytics/Analytics";
import { repository } from "~/clientInstance";
import CardTitle from "~/components/CardTitle";
import { ChipIcon, EnvironmentChip } from "~/components/Chips/index";
import DataBaseComponent from "~/components/DataBaseComponent";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import type { FormBaseComponentState } from "~/components/FormBaseComponent/FormBaseComponent";
import FormPage from "~/components/FormPage/FormPage";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import Logo from "~/components/Logo/Logo";
import Lookup from "~/components/Lookup";
import LookupResourceChipComponent from "~/components/LookupResourceChip";
import { ProjectMultiSelect } from "~/components/MultiSelect/ProjectMultiSelect";
import ExternalLink from "~/components/Navigation/ExternalLink/ExternalLink";
import { NoResults } from "~/components/NoResults/NoResults";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import type { PageContentTab } from "~/components/PageContent/PageContent";
import { PageContent } from "~/components/PageContent/PageContent";
import { SimplePagingList } from "~/components/PagingList/SimplePagingList";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import SectionNote from "~/components/SectionNote/SectionNote";
import SimpleExpander from "~/components/SimpleExpander";
import TenantVariableInputFormElement from "~/components/TenantVariableInput/TenantVariableInputRenderer";
import { withTheme } from "~/components/Theme";
import ExpansionButtons from "~/components/form/Sections/ExpansionButtons";
import { OctopusIcon, OctopusIconType } from "~/primitiveComponents/dataDisplay/Icon";
import InternalLink from "../../../components/Navigation/InternalLink/InternalLink";
import MissingVariablesIcon from "../MissingVariablesIcon/MissingVariablesIcon";
import { tenantsActions } from "../tenantsArea";
import { ScopedLibrarySetTemplateList } from "./ScopedLibrarySetTemplateList";
import styles from "./style.module.less";
import type { PageModel, InitialPageData, ModelAction } from "./tenantVariablePageModel";
import { getModelFromApi, sendModelToApi, updateModelWithCommonVariable, updateModelWithCommonVariableV2, updateModelWithProjectVariable } from "./tenantVariablePageModel";
interface LookupData {
    tenant: TenantResource;
    hasLibraryVariableSetView?: boolean;
    environments: EnvironmentResource[];
    accounts: AccountResource[];
    certificates: CertificateResource[];
    workerPools: WorkerPoolResource[];
    missingVariables: TenantMissingVariableResource[];
}
interface InitialData extends InitialPageData {
    lookupData: LookupData;
}
type MissingProjectVariableIndex = {
    [projectId: string]: {
        [environmentId: string]: string[];
    };
};
type MissingLibraryVariableIndex = {
    [libraryVariableSetId: string]: string[];
};
interface MissingVariablesIndex {
    projects: MissingProjectVariableIndex;
    libraries: MissingLibraryVariableIndex;
}
const buildMissingVariableIndex = (tenantId: string, variables: TenantMissingVariableResource[]): MissingVariablesIndex => {
    const tenantMissing = variables.find((t) => t.TenantId === tenantId);
    const projectVariables: {
        [projectId: string]: {
            [environmentId: string]: string[];
        };
    } = {};
    const libraryVariables: {
        [libraryVariableSetId: string]: string[];
    } = {};
    if (tenantMissing) {
        tenantMissing.MissingVariables.forEach((variable) => {
            if (!variable.ProjectId) {
                if (variable.LibraryVariableSetId) {
                    if (!libraryVariables[variable.LibraryVariableSetId]) {
                        libraryVariables[variable.LibraryVariableSetId] = [];
                    }
                    libraryVariables[variable.LibraryVariableSetId].push(variable.VariableTemplateId);
                }
            }
            else {
                if (!projectVariables[variable.ProjectId]) {
                    projectVariables[variable.ProjectId] = {};
                }
                if (variable.EnvironmentId) {
                    if (!projectVariables[variable.ProjectId][variable.EnvironmentId]) {
                        projectVariables[variable.ProjectId][variable.EnvironmentId] = [];
                    }
                    projectVariables[variable.ProjectId][variable.EnvironmentId].push(variable.VariableTemplateId);
                }
            }
        });
    }
    return {
        projects: projectVariables,
        libraries: libraryVariables,
    };
};
const TenantVariablesFormPage = FormPage<InitialData>();
const title = "Tenant Variables";
interface QueryParams {
    activeTab: "commonVariables" | "projectVariables";
}
interface TenantVariablesPageProps {
    tenantId: string;
    queryParams: QueryParams;
    setQueryParams: QueryParamValuesSetter<QueryParams>;
}
export const TenantVariablesPage: React.FC<TenantVariablesPageProps> = ({ tenantId, queryParams, setQueryParams }) => {
    const dispatch = useDispatch();
    const dispatchAction = useAnalyticActionDispatch();
    const onTenantVariablesFetched = useCallback((v?: TenantMissingVariableResource) => {
        dispatch(tenantsActions.tenantMissingVariablesFetched(v));
    }, [dispatch]);
    return (<TenantVariablesFormPage title={title} load={async () => {
            const hasLibraryVariableSetView = isAllowed({
                permission: Permission.LibraryVariableSetView,
                environment: "*",
                tenant: "*",
            });
            const environments = repository.Environments.all();
            const missingVariables = repository.Tenants.missingVariables({ tenantId }, true);
            const tenant = await repository.Tenants.get(tenantId);
            const pageData = await getModelFromApi(tenant);
            const accounts = pageData.containsAccounts ? repository.Accounts.all() : Promise.resolve([]);
            const certificates = pageData.containsCertificates ? repository.Certificates.listForTenant(tenantId) : Promise.resolve([]);
            const workerPools = pageData.containsWorkerPools ? repository.WorkerPools.all() : Promise.resolve([]);
            notifyTenantVariablesFetched(tenant.Id, await missingVariables, onTenantVariablesFetched);
            const lookupData: LookupData = {
                tenant,
                hasLibraryVariableSetView,
                accounts: await accounts,
                certificates: await certificates,
                workerPools: await workerPools,
                environments: await environments,
                missingVariables: await missingVariables,
            };
            return {
                ...pageData,
                lookupData,
            };
        }} renderWhenLoaded={(data: InitialData) => {
            return <TenantVariablesLayoutInternal {...data} onTenantVariablesFetched={onTenantVariablesFetched} dispatchAction={dispatchAction} queryParams={queryParams} setQueryParams={setQueryParams}/>;
        }}/>);
};
TenantVariablesPage.displayName = "TenantVariablesPage"
class LinkedEnvironmentSummary extends React.Component<{
    linkedEnvironments: string[];
    environments: EnvironmentResource[];
}, {
    showAll: boolean;
}> {
    state = { showAll: false };
    render() {
        const { linkedEnvironments, environments } = this.props;
        if (linkedEnvironments.length === 0) {
            return <span>No linked environments</span>;
        }
        const message = `Linked environment${linkedEnvironments.length === 1 ? "" : "s"}`;
        const LookupEnvironmentChip = LookupResourceChipComponent<EnvironmentResource>();
        const linkedEnvironmentResources = environments.filter((x) => linkedEnvironments.some((l) => l === x.Id));
        if (this.state.showAll) {
            const chips = linkedEnvironmentResources.map((env) => (<LookupEnvironmentChip lookupCollection={linkedEnvironmentResources} key={env.Id} lookupId={env.Id} type={ChipIcon.Environment} chipRender={(item) => <EnvironmentChip environmentName={item.Name}/>}/>));
            return (<div>
                    {message}
                    {chips}
                    <ActionButton label={`Show summary`} onClick={(e) => {
                    e.stopPropagation();
                    this.setState({ showAll: false });
                }}/>
                </div>);
        }
        else {
            const chips = take(linkedEnvironmentResources, 3).map((env) => (<LookupEnvironmentChip lookupCollection={linkedEnvironmentResources} key={env.Id} lookupId={env.Id} type={ChipIcon.Environment} chipRender={(item) => <EnvironmentChip environmentName={item.Name}/>}/>));
            const left = linkedEnvironments.length - chips.length;
            return (<div>
                    {message}
                    {chips}
                    {left > 0 && (<ActionButton label={`${left} more`} onClick={(e) => {
                        e.stopPropagation();
                        this.setState({ showAll: true });
                    }}/>)}
                </div>);
        }
    }
    static displayName = "LinkedEnvironmentSummary";
}
const libraryVariableTitle = (spaceId: string, missingVariables: string[], libraryVariableSet: TenantLibraryVariable) => {
    return (<div className={styles.cardTitle}>
            <div className={styles.libraryVariableSetContainer}>
                <CardTitle>{libraryVariableSet.LibraryVariableSetName}</CardTitle>
                <div className={styles.libraryWarning}>
                    <MissingVariablesIcon show={!!missingVariables}/>
                </div>
            </div>
            <OverflowMenu menuItems={[
            OverflowMenuItems.navItem("Edit Variable Set Template", links.variableSetPage.generateUrl({ spaceId, variableSetId: libraryVariableSet.LibraryVariableSetId }), {
                permission: Permission.VariableView,
                wildcard: true,
            }),
        ]}/>
        </div>);
};
interface TenantVariablesLayoutProps extends InitialData {
    onTenantVariablesFetched(tenantMissingVariables?: TenantMissingVariableResource): void;
    dispatchAction: AnalyticActionDispatcher;
    queryParams: QueryParams;
    setQueryParams: QueryParamValuesSetter<QueryParams>;
}
interface TenantVariablesFormState extends FormBaseComponentState<PageModel> {
    projectIds: string[];
    missingVariables: TenantMissingVariableResource[];
    accounts: AccountResource[];
    environments: EnvironmentResource[];
    projects: ProjectResource[];
    libraryVariableSets: LibraryVariableSetResource[];
    certificates?: CertificateResource[];
    workerPools?: WorkerPoolResource[];
}
function notifyTenantVariablesFetched(tenantId: string, missingVariables: TenantMissingVariableResource[], onTenantVariablesFetched: (tenantMissingVariables?: TenantMissingVariableResource) => void) {
    const found = missingVariables.find((x) => x.TenantId === tenantId);
    onTenantVariablesFetched(found);
}
class TenantVariablesLayoutInternal extends DataBaseComponent<TenantVariablesLayoutProps, TenantVariablesFormState> {
    private buildMissingVariableIndex = memoize((variables: TenantMissingVariableResource[]) => buildMissingVariableIndex(this.props.lookupData.tenant.Id, variables));
    private memoizedRepositoryCertificatesList = memoize(() => repository.Certificates.listForTenant(this.props.lookupData.tenant.Id));
    private memoizedRepositoryWorkerPoolList = memoize(() => repository.WorkerPools.all());
    constructor(props: TenantVariablesLayoutProps) {
        super(props);
        const initialModel = {
            variables: this.props.variables,
            commonVariables: this.props.commonVariables,
            projectVariables: this.props.projectVariables,
        };
        const initialMissingVariables = this.props.lookupData.missingVariables;
        const initialAccounts = this.props.lookupData.accounts;
        const initialCertificates = this.props.lookupData.certificates;
        const initialWorkerPools = this.props.lookupData.workerPools;
        const initalEnvironments = this.props.lookupData.environments;
        const initialProjects = this.props.projects ?? [];
        const initialLibraryVariableSets = this.props.libraryVariableSets ?? [];
        this.state = {
            projectIds: [],
            model: initialModel,
            cleanModel: cloneDeep(initialModel),
            missingVariables: initialMissingVariables,
            accounts: initialAccounts,
            certificates: initialCertificates,
            workerPools: initialWorkerPools,
            environments: initalEnvironments,
            projects: initialProjects,
            libraryVariableSets: initialLibraryVariableSets,
        };
    }
    handleSaveClick = async () => {
        const tenant = this.props.lookupData.tenant;
        await this.doBusyTask(async () => {
            await sendModelToApi(tenant, this.state.model);
            const variables = await getModelFromApi(tenant);
            const missingVariables = await repository.Tenants.missingVariables({ tenantId: tenant.Id }, true);
            notifyTenantVariablesFetched(this.props.lookupData.tenant.Id, missingVariables, this.props.onTenantVariablesFetched);
            this.props.dispatchAction("Save Variables", { resource: "Variable", action: Action.Save });
            this.setState({
                model: variables,
                cleanModel: cloneDeep(variables),
                missingVariables,
            });
        });
    };
    projectVariableTitle = (projectVariables: TenantProjectVariable, missingVariables: {
        [p: string]: string[];
    }, linkedEnvironments: string[]) => {
        return (<div className={styles.cardTitle}>
                <div className={styles.projectLogoContainer}>
                    <Logo url={projectVariables.Links.Logo} size="2rem"/>
                </div>
                <div className={styles.projectName}>
                    <div className={styles.projectNameAndWarningIconContainer}>
                        <div>
                            <Tooltip content={"Project: " + projectVariables.ProjectName}>
                                <CardTitle>{projectVariables.ProjectName}</CardTitle>
                            </Tooltip>
                        </div>
                        {missingVariables && (<div className={styles.projectCardWarning}>
                                <MissingVariablesIcon show={!!missingVariables}/>
                            </div>)}
                    </div>
                    <div className={cn(styles.helpText, "visible-md")}>
                        {linkedEnvironments.length} linked environment{linkedEnvironments.length === 1 ? "" : "s"}
                    </div>
                </div>
                <div style={{ flex: "1 1 20rem" }} className={cn(styles.helpText, "hide-md")}>
                    <LinkedEnvironmentSummary linkedEnvironments={linkedEnvironments} environments={this.props.lookupData.environments}/>
                </div>
                <OverflowMenu menuItems={[
                OverflowMenuItems.navItem("Edit Project Template", links.tenantVariablesProjectTemplatesPage.generateUrl({ spaceId: this.props.lookupData.tenant.SpaceId, projectSlug: projectVariables.ProjectId }), {
                    permission: Permission.VariableView,
                    wildcard: true,
                }),
            ]}/>
            </div>);
    };
    renderAllProjectVariables = (missingVariableIndex: MissingVariablesIndex) => {
        const projectVariables = this.state.model.variables.ProjectVariables;
        const projects: TenantProjectVariable[] = sortBy(Object.keys(projectVariables).map((projectId) => projectVariables[projectId]), (project) => project.ProjectName.toLowerCase());
        const projectsAsItems = Object.keys(projectVariables).map((p) => {
            const projectVariable = projectVariables[p];
            return {
                Name: projectVariable.ProjectName,
                Id: projectVariable.ProjectId,
            } as ProjectResource;
        });
        if (projects.length === 0) {
            return (<div>
                    <SectionNote>
                        Project variables allow tenants to have different values for each connected projects linked environments. Learn more about <ExternalLink href="MultiTenantVariables">tenant-specific variables</ExternalLink>.
                    </SectionNote>
                    <NoResults />
                </div>);
        }
        return (<div>
                {Object.keys(missingVariableIndex.projects).length !== 0 && this.renderMissingVariablesMessage()}
                <SectionNote>
                    Project variables allow tenants to have different values for each connected projects linked environments. Learn more about <ExternalLink href="MultiTenantVariables">tenant-specific variables</ExternalLink>.
                </SectionNote>
                <div style={{ width: "15.6rem", paddingLeft: "1rem" }}>
                    <ProjectMultiSelect items={sortBy(projectsAsItems, (project) => project.Name.toLowerCase())} value={this.state.projectIds} label="Filter by project" onChange={(projectIds) => this.setState((prevState) => ({ ...prevState, projectIds }))}/>
                </div>

                {/*
             * SimpleExpanders nested inside SimpleList items results in a janky expansion transition.
             * This change was made as a tactical fix to improve performance, we haven't made the effort to
             * fix the transition as it's lower priority
             */}
                <SimplePagingList pageSize={20} items={projects.filter((p) => this.state.projectIds.length === 0 || this.state.projectIds.indexOf(p.ProjectId) !== -1)} onRow={(item) => this.renderProjectVariable(item, missingVariableIndex)} hideItemPadding/>
            </div>);
    };
    renderMissingVariablesMessage() {
        return (<Callout type={"warning"} title="Provide missing variable values">
                If required variable values are not set it could cause the deployment to fail.
            </Callout>);
    }
    renderAllLibraryVariables = (missingVariableIndex: MissingVariablesIndex) => {
        const libraryVariables = this.state.model.variables.LibraryVariables;
        const variables = sortBy(Object.keys(libraryVariables).map((libraryVariableSetId) => libraryVariables[libraryVariableSetId]), (libraryVariableSet) => libraryVariableSet.LibraryVariableSetName).filter((libraryVariable) => libraryVariable.Templates.length > 0);
        if (variables.length === 0) {
            return (<div>
                    <SectionNote>
                        Common variables are values that remain constant across connected projects and linked environments for this tenant eg. tenant alias and contact details. Learn more about{" "}
                        <ExternalLink href="MultiTenantVariables">tenant-specific variables</ExternalLink>.
                    </SectionNote>
                    <NoResults />
                </div>);
        }
        return (<div>
                {Object.keys(missingVariableIndex.libraries).length !== 0 && this.renderMissingVariablesMessage()}
                <SectionNote>
                    Common variables are values that remain constant across connected projects and linked environments for this tenant eg. tenant alias and contact details. Learn more about{" "}
                    <ExternalLink href="MultiTenantVariables">tenant-specific variables</ExternalLink>.
                </SectionNote>
                <ExpansionButtons />
                {variables.map((v) => this.renderLibraryVariables(v, missingVariableIndex))}
            </div>);
    };
    renderAllLibraryVariablesV2 = () => {
        const tenantEnvironmentIds = Object.keys(this.props.lookupData.tenant.ProjectEnvironments).flatMap((pId) => this.props.lookupData.tenant.ProjectEnvironments[pId]);
        const tenantEnvironments = this.state.environments.filter((env) => tenantEnvironmentIds.some((envId) => envId === env.Id));
        return (<ScopedLibrarySetTemplateList spaceId={this.props.lookupData.tenant.SpaceId} libraryVariableSets={this.state.libraryVariableSets} variables={this.state.model.commonVariables} environments={tenantEnvironments} doBusyTask={this.doBusyTask} onVariableChange={(action) => this.handleLibraryVariableChangedV2(action)} variableSources={{
                accounts: {
                    items: this.state.accounts,
                    type: [AccountType.AzureServicePrincipal, AccountType.AzureSubscription, AccountType.AzureOidc],
                    onRequestRefresh: this.refreshAccounts,
                },
                certificates: {
                    items: this.getCertificates,
                    onRequestRefresh: this.refreshCertificates,
                    loadedItems: this.state.certificates ?? [],
                },
                workerPools: {
                    items: this.getWorkerPools,
                    onRequestRefresh: this.refreshWorkerPools,
                    loadedItems: this.state.workerPools ?? [],
                },
                tenantId: this.props.lookupData.tenant.Id,
            }}/>);
    };
    renderLibraryVariables = (libraryVariableSet: TenantLibraryVariable, missingVariableIndex: MissingVariablesIndex) => {
        const missingVariables = missingVariableIndex.libraries[libraryVariableSet.LibraryVariableSetId];
        const libraryVariables = libraryVariableSet.Templates.map((t) => t.Name);
        return (<SimpleExpander title={libraryVariableTitle(this.props.lookupData.tenant.SpaceId, missingVariables, libraryVariableSet)} errorKey={libraryVariableSet.LibraryVariableSetId} key={libraryVariableSet.LibraryVariableSetId}>
                <div>{libraryVariableSet.Templates.map((template) => this.renderLibraryVariable(template, libraryVariableSet.LibraryVariableSetId, libraryVariables, missingVariableIndex))}</div>
            </SimpleExpander>);
    };
    renderProjectVariable = (projectVariables: TenantProjectVariable, missingVariableIndex: MissingVariablesIndex) => {
        const linkedEnvironments = this.props.lookupData.tenant.ProjectEnvironments[projectVariables.ProjectId];
        const missingVariables = missingVariableIndex.projects[projectVariables.ProjectId];
        const tenantId = this.props.lookupData.tenant.Id;
        const linkedEnvironmentsFiltered = this.props.lookupData.environments
            .filter((env) => linkedEnvironments.includes(env.Id))
            .map((env) => {
            return {
                id: env.Id,
                sortOrder: env.SortOrder,
            };
        });
        const linkedEnvironmentsSorted = sortBy(linkedEnvironmentsFiltered, (x) => x.sortOrder).map((env) => env.id);
        return (<>
                <SimpleExpander title={this.projectVariableTitle(projectVariables, missingVariables, linkedEnvironments)} errorKey={projectVariables.ProjectId} key={projectVariables.ProjectId}>
                    <div>
                        {projectVariables.Templates.length === 0 ? (<SectionNote>There are no variable templates for this project.</SectionNote>) : linkedEnvironments.length === 0 ? (<Callout title="No environments have been selected for this project" type={"warning"}>
                                To add environments to this project go to the <InternalLink to={links.tenantOverviewPage.generateUrl({ spaceId: this.props.lookupData.tenant.SpaceId, tenantId })}>overview page</InternalLink>.
                            </Callout>) : (linkedEnvironmentsSorted.map((environmentId) => this.renderProjectEnvironmentVariable(projectVariables, environmentId, missingVariableIndex)))}
                    </div>
                </SimpleExpander>
            </>);
    };
    renderProjectEnvironmentVariable = (projectVariables: TenantProjectVariable, environmentId: string, missingVariableIndex: MissingVariablesIndex) => {
        const variableTemplates = Object.keys(projectVariables.Templates).map((templateId) => (projectVariables.Templates as any)[templateId]) as TenantVariableTemplateResource[];
        const templateNames = uniq(variableTemplates.map((p) => p.Name));
        // check we have an environment, `state.environments` may not contain it due to the users permissions
        return (<Lookup lookupCollection={this.props.lookupData.environments} lookupId={environmentId} getIdFromElement={(element: EnvironmentResource) => element.Id} render={(item: EnvironmentResource) => {
                const variableCount = variableTemplates.length;
                const titleContainer = withTheme((theme) => (<div className={styles.environmentTitleContainer}>
                            <div className={styles.environmentIconContainer}>
                                <OctopusIcon iconType={OctopusIconType.Environment}/>
                            </div>
                            <div className={styles.environmentName}>{item.Name}</div>
                            <div className={styles.environmentVariableCount}>{`(${variableCount} variable${variableCount === 1 ? "" : "s"})`}</div>
                        </div>));
                return (<SimpleExpander title={titleContainer} errorKey={item.Id} key={item.Id} isExpandedByDefault={true}>
                            <div className={styles.environmentVariablesContainer}>{variableTemplates.map((template) => this.renderVariable(template, projectVariables.ProjectId, environmentId, templateNames, missingVariableIndex))}</div>
                        </SimpleExpander>);
            }}/>);
    };
    renderLibraryVariable = (variableTemplate: TenantVariableTemplateResource, libraryVariableSetId: string, otherBindableVariables: string[], missingVariableIndex: MissingVariablesIndex) => {
        const missingVariables = (missingVariableIndex.libraries[libraryVariableSetId] || []).indexOf(variableTemplate.Id) !== -1;
        const libraryVariables = this.state.model.variables.LibraryVariables;
        return (<div className={styles.variableContainer} key={variableTemplate.Id}>
                <TenantVariableInputFormElement variableTemplate={variableTemplate} sourceItems={{
                accounts: {
                    items: this.state.accounts,
                    type: [AccountType.AzureServicePrincipal, AccountType.AzureSubscription, AccountType.AzureOidc],
                    onRequestRefresh: this.refreshAccounts,
                },
                certificates: {
                    items: this.getCertificates,
                    onRequestRefresh: this.refreshCertificates,
                },
                workerPools: {
                    items: this.getWorkerPools,
                    onRequestRefresh: this.refreshWorkerPools,
                },
                tenantId: this.props.lookupData.tenant.Id,
            }} localNames={otherBindableVariables} doBusyTask={this.doBusyTask} value={libraryVariables[libraryVariableSetId].Variables[variableTemplate.Id]} warning={missingVariables ? "Value required for deployment" : undefined} onChange={(newVal) => this.handleLibraryVariableChanged(libraryVariableSetId, variableTemplate.Id, newVal)}/>
            </div>);
    };
    handleLibraryVariableChanged = (libraryVariableSetId: string, displayPropsId: string, value: any) => {
        this.setState((state) => ({
            model: updateModelWithCommonVariable(state.model, libraryVariableSetId, displayPropsId, value),
        }));
    };
    handleLibraryVariableChangedV2 = (action: ModelAction) => {
        this.setState((state) => ({
            model: updateModelWithCommonVariableV2(state.model, action),
        }));
    };
    renderVariable = (variableTemplate: TenantVariableTemplateResource, projectId: string, environmentId: string, otherBindableVariables: string[], missingVariableIndex: MissingVariablesIndex) => {
        const missingVariables = ((missingVariableIndex.projects[projectId] || {})[environmentId] || []).indexOf(variableTemplate.Id) !== -1;
        const projectVariables = this.state.model.variables.ProjectVariables;
        return (<div className={styles.variableContainer} key={variableTemplate.Id}>
                <TenantVariableInputFormElement variableTemplate={variableTemplate} sourceItems={{
                accounts: {
                    items: this.state.accounts,
                    type: [AccountType.AzureServicePrincipal, AccountType.AzureSubscription, AccountType.AzureOidc],
                    onRequestRefresh: this.refreshAccounts,
                },
                certificates: {
                    items: this.getCertificates,
                    onRequestRefresh: this.refreshCertificates,
                },
                workerPools: {
                    items: this.getWorkerPools,
                    onRequestRefresh: this.refreshWorkerPools,
                },
                tenantId: this.props.lookupData.tenant.Id,
            }} localNames={otherBindableVariables} doBusyTask={this.doBusyTask} value={projectVariables[projectId].Variables[environmentId][variableTemplate.Id]} warning={missingVariables ? "Value required for deployment" : undefined} onChange={(newVal) => this.handleProjectVariableChanged(projectId, environmentId, variableTemplate.Id, newVal)}/>
            </div>);
    };
    handleProjectVariableChanged = (projectId: string, environmentId: string, displayPropsId: string, value: any) => {
        this.setState((state) => ({
            model: updateModelWithProjectVariable(state.model, projectId, environmentId, displayPropsId, value),
        }));
    };
    render() {
        const missingVariableIndex = this.buildMissingVariableIndex(this.state.missingVariables);
        const missingCommonVariables = this.state.model.commonVariables.some((v) => v.Status === "missing");
        return (<LegacyForm onSaveClick={this.handleSaveClick} savePermission={{
                permission: Permission.VariableEdit,
                tenant: this.props.lookupData.tenant.Id,
                wildcard: true,
            }} cleanModel={this.state.cleanModel} model={this.state.model} saveText="Tenant details updated">
                {({ FormContent, createSaveAction }) => {
                const commonVariablesTabV1: PageContentTab<"commonVariables"> = {
                    value: "commonVariables",
                    label: "Common Variables",
                    content: <FormContent hideExpandAll={true}>{this.renderAllLibraryVariables(missingVariableIndex)}</FormContent>,
                    warning: Object.keys(missingVariableIndex.libraries).length > 0 ? `Some of the required variable values have not been set` : undefined,
                };
                const commonVariablesTabV2: PageContentTab<"commonVariables"> = {
                    value: "commonVariables",
                    label: "Common Variables",
                    content: <FormContent hideExpandAll={true}>{this.renderAllLibraryVariablesV2()}</FormContent>,
                    warning: missingCommonVariables ? `Some of the required variable values have not been set` : undefined,
                };
                const commonVariablesTab = isFeatureToggleEnabled("CommonVariableScopingFeatureToggle") ? commonVariablesTabV2 : commonVariablesTabV1;
                const projectVariablesTab: PageContentTab<"projectVariables"> = {
                    value: "projectVariables",
                    label: "Project Variables",
                    content: <FormContent hideExpandAll={true}>{this.renderAllProjectVariables(missingVariableIndex)}</FormContent>,
                    warning: Object.keys(missingVariableIndex.projects).length > 0 ? `Some of the required variable values have not been set` : undefined,
                };
                return (<PageContent header={{ primaryAction: createSaveAction({}), title: "Tenant Variables" }} busy={this.state.busy} errors={this.errors} currentTab={this.props.queryParams.activeTab} onTabChanged={(value) => this.props.setQueryParams((prev) => ({ ...prev, activeTab: value }))} tabs={this.props.lookupData.hasLibraryVariableSetView ? [commonVariablesTab, projectVariablesTab] : [projectVariablesTab]}/>);
            }}
            </LegacyForm>);
    }
    private refreshAccounts = async () => {
        await this.doBusyTask(async () => {
            this.setState({ accounts: await repository.Accounts.all() });
        });
    };
    private getCertificates = async () => {
        let certificates = this.state.certificates;
        if (!certificates) {
            certificates = await this.memoizedRepositoryCertificatesList();
            this.setState({ certificates });
        }
        return certificates;
    };
    private refreshCertificates = () => {
        return this.doBusyTask(async () => {
            this.setState({ certificates: await repository.Certificates.listForTenant(this.props.lookupData.tenant.Id) });
        });
    };
    private getWorkerPools = async () => {
        let workerPools = this.state.workerPools;
        if (!workerPools) {
            workerPools = await this.memoizedRepositoryWorkerPoolList();
            this.setState({ workerPools });
        }
        return workerPools;
    };
    private refreshWorkerPools = () => {
        return this.doBusyTask(async () => {
            this.setState({ workerPools: await repository.WorkerPools.all() });
        });
    };
    static displayName = "TenantVariablesLayoutInternal";
}
